import { useEffect, useState, useCallback } from "react";
import { DataTable, ColumnDef, FilterOption } from "@/components/DataTable";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, Download } from "lucide-react";
import { api, ApiClientError } from "@/lib/api";

// Define the Domain type (matches backend API)
interface Domain {
  id: string;
  domain: string;
  ncii_instances: number;
  hosting_provider: string;
  registrar: string;
  active_urls: number;
  removed_urls: number;
  takedown_rate: number; // Percentage as decimal (e.g., 0.85 for 85%)
  avg_response_time: number; // In days
  technologies: string[]; // Array of technology names
}

const PAGE_SIZE = 50;

export function DomainList() {
  const { logout } = useAuth();
  const [domains, setDomains] = useState<Domain[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [totalCount, setTotalCount] = useState<number>(0);

  const loadDomains = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const offset = (currentPage - 1) * PAGE_SIZE;
      const data = await api.domains.list(PAGE_SIZE, offset, true);
      
      if (typeof data === "object" && "items" in data) {
        // Paginated response
        setDomains(data.items);
        setHasMore(data.has_next_page);
        setTotalCount(data.total);
      } else {
        // Array response
        setDomains(data);
        setHasMore(data.length === PAGE_SIZE);
        setTotalCount(data.length);
      }
    } catch (err) {
      if (err instanceof ApiClientError) {
        if (err.status === 401) {
          logout();
        } else {
          setError(err.detail || "Failed to load domains");
        }
      } else {
        setError("Failed to load domains");
      }
    } finally {
      setLoading(false);
    }
  }, [currentPage, logout]);

  useEffect(() => {
    loadDomains();
  }, [loadDomains]);

  const handleNextPage = () => {
    if (!loading && hasMore) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handlePreviousPage = () => {
    if (!loading && currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  // Define columns for the Domains table
  const columns: ColumnDef<Domain>[] = [
    {
      key: "domain",
      header: "Domain",
      render: (item) => (
        <div className="text-xs font-medium text-foreground">{item.domain}</div>
      ),
      sortValue: (item) => item.domain,
    },
    {
      key: "ncii_instances",
      header: "NCII Instances",
      render: (item) => (
        <div className="text-xs text-muted-foreground">
          {item.ncii_instances}
        </div>
      ),
      sortValue: (item) => item.ncii_instances,
    },
    {
      key: "hosting_provider",
      header: "Hosting Provider",
      render: (item) => (
        <div className="text-xs text-muted-foreground">
          {item.hosting_provider}
        </div>
      ),
      sortValue: (item) => item.hosting_provider,
    },
    {
      key: "registrar",
      header: "Registrar",
      render: (item) => (
        <div className="text-xs text-muted-foreground">{item.registrar}</div>
      ),
      sortValue: (item) => item.registrar,
    },
    {
      key: "active_urls",
      header: "Active URLs",
      render: (item) => (
        <div className="text-xs text-muted-foreground">{item.active_urls}</div>
      ),
      sortValue: (item) => item.active_urls,
    },
    {
      key: "removed_urls",
      header: "Removed URLs",
      render: (item) => (
        <div className="text-xs text-muted-foreground">{item.removed_urls}</div>
      ),
      sortValue: (item) => item.removed_urls,
    },
    {
      key: "takedown_rate",
      header: "Takedown Rate",
      render: (item) => (
        <div className="text-xs text-muted-foreground">
          {(item.takedown_rate * 100).toFixed(1)}%
        </div>
      ),
      sortValue: (item) => item.takedown_rate,
    },
    {
      key: "avg_response_time",
      header: "Avg Response Time",
      render: (item) => (
        <div className="text-xs text-muted-foreground">
          {item.avg_response_time.toFixed(1)} days
        </div>
      ),
      sortValue: (item) => item.avg_response_time,
    },
    {
      key: "technologies",
      header: "Technologies",
      render: (item) => (
        <div className="text-xs text-muted-foreground">
          {item.technologies.join(", ")}
        </div>
      ),
      sortValue: (item) => item.technologies.join(", "),
    },
    {
      key: "actions",
      header: "Actions",
      sortable: false,
      render: (item) => (
        <Button
          variant="outline"
          size="sm"
          className="h-7 px-3"
          onClick={() => {
            // TODO: Navigate to domain details or open modal
            console.log("View domain:", item.id);
          }}
        >
          View
        </Button>
      ),
    },
  ];

  // Define filters (for search functionality)
  const filters: FilterOption[] = [
    { label: "Hosting Provider", placeholder: "All Providers" },
    { label: "Registrar", placeholder: "All Registrars" },
    { label: "Technology", placeholder: "All Technologies" },
  ];

  // Handle new domain creation
  const handleNewDomain = () => {
    // TODO: Implement new domain modal or navigation
    console.log("Add new domain");
  };

  // Handle export
  const handleExport = () => {
    // TODO: Implement export functionality
    console.log("Export domains");
  };

  // Action buttons for the header
  const actionButtons = (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={handleExport}
        className="h-9 px-4"
      >
        <Download className="h-4 w-4 mr-2" />
        Export
      </Button>
      <Button
        size="sm"
        onClick={handleNewDomain}
        className="h-9 px-4"
      >
        <Plus className="h-4 w-4 mr-2" />
        Add Domain
      </Button>
    </>
  );

  return (
    <DataTable
      title="Domain Management"
      subtitle="Track and manage domains hosting illegal content"
      columns={columns}
      data={domains}
      loading={loading}
      error={error}
      filters={filters}
      searchPlaceholder="Search domains..."
      onNextPage={handleNextPage}
      onPreviousPage={handlePreviousPage}
      hasNextPage={hasMore}
      hasPreviousPage={currentPage > 1}
      currentPage={currentPage}
      itemsPerPage={PAGE_SIZE}
      actionButtons={actionButtons}
      totalCount={totalCount}
      entityName="domains"
    />
  );
}



